<template>
  <v-main class="plan-view-main">
    <v-container fluid class="pa-6">
      <!-- Action Toolbar -->
      <v-card class="modern-card mb-6" elevation="1" v-if="!loadingPlan && !isPrint">
        <v-card-text class="pa-4">
          <div class="d-flex flex-wrap gap-3">
            <v-btn
                :to="{ name: 'planedit', params: { id: plan.id } }"
                variant="outlined"
                class="action-btn"
                v-if="isSharedView === false && !isPrint"
            >
              Edit Plan
            </v-btn>
            <v-btn
                :href="'/team/'+ plan.team.id + '/plan/' + plan.id"
                variant="outlined"
                class="action-btn"
                v-if="!isPrint"
            >
              Print
            </v-btn>
            <v-btn
                @click="duplicate"
                variant="outlined"
                class="action-btn"
                v-if="!isPrint"
            >
              Duplicate
            </v-btn>
            <v-btn
                @click="deleteReport"
                color="error"
                variant="outlined"
                class="action-btn"
                v-if="!isPrint"
            >
              Delete
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Loading Progress -->
      <v-progress-linear
          :active="loadingPlan"
          color="primary"
          height="3"
          indeterminate
          class="mb-6"
      ></v-progress-linear>

      <!-- Plan Header -->
      <v-card class="modern-card mb-6" elevation="1">
        <v-card-text class="pa-6">
          <div class="plan-header-layout">
            <div class="plan-header-content">
              <h1 class="plan-title">{{ plan.name }}</h1>
              <div class="plan-meta">
                <div class="meta-item">
                  <DateTimeDisplay :time="plan.date" class="meta-text"></DateTimeDisplay>
                </div>
                <div class="meta-item" v-if="plan.team">
                  <span class="meta-label">Team:</span>
                  <span class="meta-text">{{ plan.team.name }}</span>
                </div>
              </div>
              <div class="plan-notes" v-if="plan.notes">
                {{ plan.notes }}
              </div>
            </div>

            <!-- Share Section -->
            <div class="share-section-header" v-if="shareCodeUrl && !isSharedView && !isPrint">
              <div class="share-item-header">
                <span class="share-label-header">Share this practice plan:</span>
                <v-btn variant="outlined" size="small" @click="copyShareUrl()" class="copy-btn-header">
                  Copy Link
                </v-btn>
              </div>
              <div class="share-item-header">
                <span class="share-label-header">Share next practice for team:</span>
                <v-btn variant="outlined" size="small" @click="copyTeamShareUrl()" class="copy-btn-header">
                  Copy Link
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Practice Plan Content -->
      <v-card class="modern-card" elevation="1" v-if="!loadingPlan">
        <v-card-title class="card-header">
          Practice Plan
        </v-card-title>
        <v-card-text class="pa-0">
          <div class="time-slots">
            <div
                class="time-slot"
                v-for="(timeSlot, index) in plan.timeSlots"
                :key="timeSlot"
                :class="{ 'break-slot': timeSlot.isBreak }"
            >
              <div class="time-display">
                <TimeDisplay
                    :practiceStartTime="plan.date"
                    :timeSlots="plan.timeSlots"
                    :index="index"
                    :duration="timeSlot.duration"
                ></TimeDisplay>
              </div>
              <div class="time-slot-content">
                <div v-if="timeSlot.isBreak" class="break-content">
                  <span class="break-text">Water Break</span>
                </div>
                <div v-else class="drills-content">
                  <p v-if="timeSlot.description && !(timeSlot.drills && timeSlot.drills.length)" class="slot-plan-note">
                    {{ timeSlot.description }}
                  </p>
                  <div
                      class="drill-item"
                      v-for="drill in timeSlot.drills"
                      :key="drill"
                  >
                    <DrillView :drill="drill" :teamId="plan.team.id" :showImages="true"></DrillView>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import TimeDisplay from '../components/TimeDisplay.vue'
import DateTimeDisplay from '../components/DateTimeDisplay.vue'

import api from '../../api'
import DrillView from "./DrillView.vue";

export default {
  name: 'PracticePlanView',
  components: {
    TimeDisplay,
    DateTimeDisplay,
    DrillView,
  },
  created() {
    // watch the params of the route to fetch the data again
    this.$watch(
        () => {
          this.isSharedView = this.$route.name === "sharedview";
          this.isPrint = this.$route.name === "planviewprint";
        },
        () => {
          this.isSharedView = this.$route.name === "sharedview";
          this.isPrint = this.$route.name === "planviewprint";
          this.fetchData(this.$route.params.id);
        },
        {immediate: true}
    )
  },
  methods: {
    deleteReport() {

      api.practicePlans.delete(this.plan.team.id, this.plan.id, () => {
        this.$router.push({name: 'home'})
      }, () => {
        this.errorMessage = "Unable to delete plan"
      });

    },
    duplicate() {

      let copiedPlan = JSON.parse(JSON.stringify(this.plan))
      copiedPlan.id = "new";
      copiedPlan.name = "Duplicate " + copiedPlan.name;

      api.practicePlans.save(copiedPlan.team.id, copiedPlan, (savedPlan) => {
        this.$toast.open({message: 'Drill copied', type: 'success'});
        this.$router.push({name: 'planview', params: {id: savedPlan.id}})
        this.plan = savedPlan;

      }, () => {
        this.errorMessage = "Unable to duplicate plan"
      });


    },
    copyShareUrl() {
      navigator.clipboard.writeText(this.shareCodeUrl);

      this.$toast.open({message: 'URL Copied', type: 'success'});
    },
    copyTeamShareUrl() {
      navigator.clipboard.writeText(this.teamShareCodeUrl);

      this.$toast.open({message: 'URL Copied', type: 'success'});
    },
    fetchData(id) {
      this.error = null;
      this.loadingPlan = true;

      let planHandler = api.practicePlans.get;

      if (this.isSharedView) {
        planHandler = api.practicePlans.getShared;
      }

      planHandler(id, (plan) => {
        this.isPrint = this.$route.name === "planviewprint";
        this.loadingPlan = false;

        if (plan.timeSlots) {
          plan.timeSlots.forEach((t) => {
            if (!t.drills) {
              return
            }
            t.drills.forEach((d) => {
              if (!d.links) {
                return
              }
              d.links.forEach((l) => {
                let url = l.url.trim();
                if (url.indexOf("http") != 0) {
                  url = "https://" + url;
                }

                l.url = url;
              });
            });
          });
        }


        this.plan = plan;

        if (!this.isSharedView) {
          this.shareCodeUrl = `https://${window.location.host}/share/${plan.shareCode}`
          this.teamShareCodeUrl = `https://${window.location.host}/share/team/${plan.team.id}`
        }
      }, () => {
        this.loadingPlan = false;
      })
    }
  },
  data() {
    return {
      plan: {},
      shareCodeUrl: "",
      teamShareCodeUrl: "",
      loadingPlan: false,
      isPrint: false,
    }
  }

}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

/* Main layout */
.plan-view-main {
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  min-height: 100vh;
}

/* Modern card styling */
.modern-card {
  border-radius: var(--radius-none) !important;
  box-shadow: var(--shadow-sm) !important;
  transition: all 0.3s ease;
  border: 1px solid var(--border-subtle);
}

.modern-card:hover {
  transform: none;
  box-shadow: var(--shadow-sm) !important;
}

.card-header {
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  border-bottom: 1px solid var(--border-subtle);
  font-weight: 600;
  padding: var(--space-4) var(--space-6);
  font-size: 1.1rem;
}

/* Action buttons */
.action-btn {
  border-radius: var(--radius-none) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: none;
}

/* Plan header styling */
.plan-header-layout {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.plan-header-content {
  flex: 1;
  text-align: left;
}

.plan-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.9);
  margin-bottom: 16px;
  line-height: 1.2;
}

.plan-meta {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 16px;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.meta-label {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
}

.meta-text {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.9);
}

.plan-notes {
  font-size: 1.1rem;
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.6;
  text-align: left;
  max-width: none;
  margin: 0;
}

/* Share section styling */
.share-section-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 300px;
  max-width: 400px;
}

.share-item-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0;
}

.share-label-header {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  font-size: 0.85rem;
  white-space: nowrap;
}

.share-url-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0;
}

.copy-btn-header {
  border-radius: var(--radius-none) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  min-width: 80px !important;
  font-size: 0.8rem !important;
}

/* Legacy share section (kept for compatibility) */
.share-section {
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex-wrap: wrap;
}

.share-item {
  flex: 1;
  min-width: 300px;
}

.share-label {
  display: block;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.8);
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.share-url {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: var(--space-3) var(--space-4);
  background: rgba(0, 0, 0, 0.05);
  border-radius: var(--radius-none);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.url-text {
  flex: 1;
  font-family: var(--font-mono);
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.8);
  word-break: break-all;
  background: transparent;
  padding: 0;
}

.copy-btn {
  border-radius: var(--radius-none) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  min-width: 60px !important;
}

/* Time slots styling */
.time-slots {
  background: white;
}

.time-slot {
  display: flex;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: background-color 0.2s ease;
}

.time-slot:last-child {
  border-bottom: none;
}

.time-slot:hover {
  background: rgba(0, 0, 0, 0.02);
}

.break-slot {
  background: rgba(0, 0, 0, 0.05);
}

.break-slot:hover {
  background: rgba(0, 0, 0, 0.08);
}

.time-display {
  min-width: 120px;
  padding: 20px 16px;
  background: rgba(0, 0, 0, 0.03);
  border-right: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-slot-content {
  flex: 1;
  padding: 20px 24px;
}

.break-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.break-text {
  font-size: 1.2rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.drills-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.drill-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-bottom: 16px;
}

.drill-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

/* Progress bar styling */
.v-progress-linear {
  border-radius: var(--radius-none) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .plan-header-layout {
    flex-direction: column;
    gap: 20px;
  }

  .plan-title {
    font-size: 2rem;
  }

  .plan-meta {
    align-items: flex-start;
  }

  .meta-item {
    justify-content: flex-start;
  }

  .time-slot {
    flex-direction: column;
  }

  .time-display {
    min-width: auto;
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding: 12px 16px;
  }

  .time-slot-content {
    padding: 16px;
  }

  .share-section-header {
    min-width: auto;
    max-width: none;
  }

  .share-section {
    flex-direction: column;
    gap: 16px;
  }

  .share-item {
    min-width: auto;
    flex: none;
  }

  .share-url {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .copy-btn {
    align-self: flex-end;
  }
}

.slot-plan-note {
  margin: 0;
  padding: var(--space-3) var(--space-4);
  font-size: 14px;
  font-style: italic;
  color: var(--ink-muted);
}

</style>
  